import { useRef, useState } from "react";
import ChartWidget from "./ChartWidget";
import KpiWidget from "./KpiWidget";
import CopyButton, { chartText, kpiText, plainText, tableText } from "./CopyButton";
import TableWidget from "./TableWidget";
import CompetitiveTab from "./CompetitiveTab";
import { mdToHtml } from "../utils/markdown";

// A small table built from a chart's own labels/series — lets a chart be inspected as raw numbers
// without leaving the widget, mirroring what the model saw when it built the chart.
function ChartAsTable({ spec }) {
  const labels = spec.labels || [];
  const series = spec.series || [];
  return (
    <div className="table-card">
      <div className="table-box">
        <table>
          <thead>
            <tr>
              <th></th>
              {series.map((s) => (
                <th key={s.name}>{s.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((label, i) => (
              <tr key={label}>
                <td>{label}</td>
                {series.map((s) => (
                  <td key={s.name}>{s.data?.[i]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Narration({ text }) {
  return (
    <div className="narration-box">
      {text && text.trim() ? (
        <div dangerouslySetInnerHTML={{ __html: mdToHtml(text) }} />
      ) : (
        <div className="narration-empty">No narration was captured for this widget.</div>
      )}
    </div>
  );
}

/** Indian-notation short form, for figures that are routinely in the billions here. No currency
 *  symbol: the widget knows the magnitude of its numbers but not their unit. */
function compact(n) {
  if (typeof n !== "number" || !isFinite(n)) return String(n ?? "—");
  const a = Math.abs(n);
  if (a >= 1e7) return (n / 1e7).toFixed(2).replace(/\.00$/, "") + " Cr";
  if (a >= 1e5) return (n / 1e5).toFixed(2).replace(/\.00$/, "") + " L";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// A first-to-last delta only means something when the axis is ORDERED. Charted labels here are either
// time buckets (aggregate's `bucket`, which comes back chronological) or categories — and a category
// axis is usually sorted by value, where "first to last" is just "top minus bottom" dressed up as a
// trend. Reported only for labels that are actually time periods: 2026, 2026-03, 2026-03-14.
// Two shapes reach the axis: aggregate's own buckets (2026, 2026-03, 2026-03-14) and the month names
// a model writes when it builds the series itself ("Jan 2025", "Mar-2026").
const PERIOD_RE = /^\d{4}(-\d{1,2}){0,2}$/;
const MONTH_RE = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[\s-]+\d{4}$/i;
const isTimeAxis = (labels) =>
  labels.length > 1 &&
  labels.every((l) => PERIOD_RE.test(String(l).trim()) || MONTH_RE.test(String(l).trim()));

/** Per-series facts a reader would otherwise have to work out by squinting at the plot. */
function seriesStats(labels, s) {
  const nums = (s.data || []).map((v) => (typeof v === "number" && isFinite(v) ? v : null));
  const present = nums.map((v, i) => [v, i]).filter(([v]) => v !== null);
  if (!present.length) return null;
  const total = present.reduce((t, [v]) => t + v, 0);
  const [maxV, maxI] = present.reduce((b, c) => (c[0] > b[0] ? c : b));
  const [minV, minI] = present.reduce((b, c) => (c[0] < b[0] ? c : b));
  const first = present[0][0];
  const last = present[present.length - 1][0];
  // Whether this is worth showing at all is decided by isTimeAxis at render time.
  const change = first !== 0 ? ((last - first) / Math.abs(first)) * 100 : null;
  return {
    name: s.name || "value",
    total, maxV, minV,
    maxLabel: labels[maxI] ?? "—",
    minLabel: labels[minI] ?? "—",
    change, negatives: present.filter(([v]) => v < 0).length,
  };
}

/** What this chart or table actually shows, read off its own numbers.
 *
 * Deliberately computed, not written by a model. The Narration tab already carries the assistant's
 * prose, but that is whatever it happened to be saying during the turn — on a multi-chart answer the
 * same paragraph lands on every widget, so four different charts can share one narration that
 * describes none of them. This is per-widget and always true of the data in front of you. */
function Summary({ kind, spec }) {
  if (kind !== "chart") {
    const cols = spec.columns || [];
    return (
      <div className="narration-box query-box">
        <div className="summary-line">
          A table of <strong>{(spec.returned ?? (spec.rows || []).length).toLocaleString("en-IN")}</strong> row
          {(spec.returned ?? (spec.rows || []).length) === 1 ? "" : "s"} and <strong>{cols.length}</strong> column
          {cols.length === 1 ? "" : "s"}.
        </div>
        {cols.length > 0 && (
          <div className="query-row">
            <span className="query-key">Columns</span>
            <span className="query-val">{cols.map((c) => <code key={c} className="query-table">{c}</code>)}</span>
          </div>
        )}
      </div>
    );
  }

  const labels = spec.labels || [];
  const stats = (spec.series || []).map((s) => seriesStats(labels, s)).filter(Boolean);
  if (!labels.length || !stats.length) {
    return <div className="narration-box"><div className="narration-empty">This chart has no data to describe.</div></div>;
  }

  return (
    <div className="narration-box query-box">
      <div className="summary-line">
        A <strong>{spec.type || "bar"}</strong> chart of{" "}
        <strong>{stats.map((s) => s.name).join(" and ")}</strong> across{" "}
        <strong>{labels.length}</strong> {labels.length === 1 ? "point" : "points"}
        {labels.length > 1 && <> (<code>{labels[0]}</code> to <code>{labels[labels.length - 1]}</code>)</>}.
      </div>

      {stats.map((s) => (
        <div key={s.name} className="summary-series">
          {stats.length > 1 && <div className="summary-series-name">{s.name}</div>}
          <table className="query-args">
            <tbody>
              <tr><td className="query-args-key">Total</td><td className="query-args-val">{compact(s.total)}</td></tr>
              <tr>
                <td className="query-args-key">Highest</td>
                <td className="query-args-val">{compact(s.maxV)} <span className="query-muted">at {s.maxLabel}</span></td>
              </tr>
              <tr>
                <td className="query-args-key">Lowest</td>
                <td className="query-args-val">{compact(s.minV)} <span className="query-muted">at {s.minLabel}</span></td>
              </tr>
              {s.change !== null && isTimeAxis(labels) && (
                <tr>
                  <td className="query-args-key">First → last</td>
                  <td className="query-args-val">
                    {s.change >= 0 ? "+" : ""}{s.change.toFixed(1)}%{" "}
                    <span className="query-muted">({labels[0]} → {labels[labels.length - 1]})</span>
                  </td>
                </tr>
              )}
              {s.negatives > 0 && (
                // Worth calling out rather than leaving to be spotted: a negative in a sales series
                // usually means cancellations or reversals, and it changes how the total reads.
                <tr>
                  <td className="query-args-key">Note</td>
                  <td className="query-args-val">
                    {s.negatives} negative value{s.negatives === 1 ? "" : "s"} in this series.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/** How one tool argument reads in the Query tab. Objects (a `filters` map) are shown as JSON rather
 *  than "[object Object]"; everything else is printed as-is. */
function argValue(v) {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v) || typeof v === "object") return JSON.stringify(v);
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

/** Which tables a widget's numbers came from, and the query that produced them.
 *
 * Built server-side from the same `_source` entry a refresh replays (widget_replay.provenance), so
 * what is shown here is the call that would actually run again — not a description written alongside
 * it that could drift. Empty arguments are dropped: a widget's query is easier to read as the three
 * fields that were set than as every field the tool accepts. */
function Query({ q }) {
  if (!q) {
    return <div className="narration-box"><div className="narration-empty">No query was recorded for this widget.</div></div>;
  }
  const args = Object.entries(q.args || {})
    .map(([k, v]) => [k, argValue(v)])
    .filter(([, v]) => v !== null);
  return (
    <div className="narration-box query-box">
      <div className="query-row">
        <span className="query-key">Dataset</span>
        <span className="query-val">{q.dataset || <em className="query-muted">raw SQL — no single dataset</em>}</span>
      </div>
      <div className="query-row">
        <span className="query-key">Tables</span>
        <span className="query-val">
          {(q.tables || []).length
            ? (q.tables || []).map((t) => <code key={t} className="query-table">{t}</code>)
            : <em className="query-muted">not recorded</em>}
        </span>
      </div>
      <div className="query-row">
        <span className="query-key">Built with</span>
        <span className="query-val"><code>{q.tool || "—"}</code></span>
      </div>

      {q.sql ? (
        <>
          <div className="query-label">SQL</div>
          <pre className="query-sql">{q.sql}</pre>
        </>
      ) : args.length ? (
        <>
          <div className="query-label">Query</div>
          <table className="query-args">
            <tbody>
              {args.map(([k, v]) => (
                <tr key={k}>
                  <td className="query-args-key">{k}</td>
                  <td className="query-args-val"><code>{v}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <div className="query-muted" style={{ marginTop: 8 }}>No arguments were passed.</div>
      )}
    </div>
  );
}

/**
 * A saved/historical chart or table, shown with Visual/Table/Narration/Query tabs so it stays legible
 * once detached from the live chat that produced it (a dashboard, or a reopened past conversation). Live,
 * in-flight turns render ChartWidget/TableWidget directly instead — the narration is already the
 * visible text right below the widget in that case, so a redundant tab adds nothing.
 */
const CHART_TYPES = ["bar", "line", "pie"];

/**
 * `selectable`/`selected`/`onToggle` turn the card into a pickable tile — used by the dashboard
 * builder, where the user ticks exactly which widgets become the dashboard. Omitted everywhere else,
 * so a plain <WidgetCard kind spec /> renders exactly as it always did.
 *
 * `onChartTypeChange` reports the type chosen in the header select back to the parent. Normally the
 * choice is display-only (see below), but the builder persists it: picking "line" for a date-series
 * chart there is the user doing by hand what the removed AI curator used to guess at.
 *
 * `competitive` adds the Competitive tab (rivals' public figures over the card's period). Only the
 * saved dashboard passes it — a reopened chat or a builder tile is left exactly as it was.
 */
export default function WidgetCard({ kind, spec, selectable = false, selected = false, onToggle,
                                     onChartTypeChange, competitive = false, onRemoveKpiCard }) {
  const isChart = kind === "chart";
  const isKpi = kind === "kpi";
  // Query last: it answers "where did this come from", which is a follow-up question to seeing
  // the number, not the first thing a reader wants.
  // Summary sits right after the picture: "what am I looking at" is the first question a card
  // detached from its chat has to answer. Query is last - provenance is the follow-up.
  // KPI cards are their own summary and have no rows behind them, so they skip Summary and Table — and
  // Query: a KPI row is read by business users, for whom the underlying queries are noise.
  const baseTabs = isChart
    ? ["Visual", "Summary", "Table", "Narration", "Query"]
    : isKpi
      ? ["Visual", "Narration"]
      : ["Table", "Summary", "Narration", "Query"];
  // Right after Narration: having read what the numbers say, the next question is how rivals did.
  const tabs = competitive && !isKpi
    ? baseTabs.flatMap((t) => (t === "Narration" ? [t, "Competitive"] : [t]))
    : baseTabs;
  const [tab, setTab] = useState(tabs[0]);
  const bodyRef = useRef(null);
  // Re-rendering the same numbers as a different chart type is a display choice, so it lives here in
  // the client — it never re-queries, and the saved spec on the server is left untouched (unless a
  // parent passes onChartTypeChange and chooses to persist it).
  const [chartType, setChartType] = useState(spec.type || "bar");

  const shownSpec = isChart ? { ...spec, type: chartType } : spec;

  const pickType = (t) => {
    setChartType(t);
    onChartTypeChange?.(t);
  };

  return (
    <div className={"widget-card" + (isKpi ? " widget-card-kpi" : "") + (selectable && !selected ? " widget-card-unpicked" : "")}>
      <div className="widget-bar">
        {selectable && (
          // Label-wrapped so the whole hit area toggles, not just the 13px box itself.
          <label className="widget-pick" title={selected ? "Included — click to exclude" : "Excluded — click to include"}>
            <input type="checkbox" checked={selected} onChange={onToggle} />
          </label>
        )}
        {tabs.map((t) => (
          <button key={t} className={"widget-tab" + (t === tab ? " active" : "")} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
        {isChart && tab === "Visual" && (
          <select
            className="widget-select"
            value={chartType}
            onChange={(e) => pickType(e.target.value)}
            title="Chart type"
          >
            {CHART_TYPES.map((t) => (
              <option key={t} value={t}>
                {t[0].toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="widget-tab-body" ref={bodyRef}>
        {tab !== "Competitive" && (
          <CopyButton
            className="on-tab"
            title={`Copy ${tab.toLowerCase()}`}
            getText={() => {
              if (tab === "Narration") return plainText(spec.narration || "");
              if (tab === "Query") return JSON.stringify(spec._query || {}, null, 2);
              if (isKpi) return kpiText(spec);
              if (isChart && tab !== "Summary") return chartText(spec);
              // The Summary tab is computed in this component; copy it as the reader sees it, minus this button.
              if (tab === "Summary") return (bodyRef.current?.innerText || "").replace(/^\s*[⧉✓]\s*/, "");
              return tableText(spec.columns || [], spec.rows || []);
            }}
          />
        )}
        {tab === "Visual" && (isKpi ? <KpiWidget spec={spec} onRemoveCard={onRemoveKpiCard} /> : <ChartWidget spec={shownSpec} />)}
        {tab === "Table" && (isChart ? <ChartAsTable spec={spec} /> : <TableWidget spec={spec} />)}
        {tab === "Summary" && <Summary kind={kind} spec={spec} />}
        {tab === "Narration" && <Narration text={spec.narration} />}
        {tab === "Competitive" && <CompetitiveTab spec={spec} />}
        {tab === "Query" && <Query q={spec._query} />}
      </div>
    </div>
  );
}
